// 登録済みルート要素 → 登録済みセレクタキー集合
// (グローバル汚染を避けるためモジュールスコープの WeakMap で管理)
const REGISTERED_ROOTS = new WeakMap();

export default class ImageZoomer {
  static SELECTORS = {
    general: { selector: '[data-lightbox]', attr: 'href' },
    // エディタ本文内のリンクなし画像と空リンク画像を1つのギャラリーとして統合
    editorContent: { selector: ':not(a) > img, a[href=\'\'] > img', attr: 'src' },
  };

  setGeneralEvent(rootElement = document) {
    this._initDelegate(rootElement, [ImageZoomer.SELECTORS.general]);
  }

  setEditorEvent(rootElement = document) {
    if (!rootElement) return;

    this._initDelegate(rootElement, [ImageZoomer.SELECTORS.editorContent]);
  }

  _initDelegate(rootElement, selectorConfigs) {
    const selectorKey = selectorConfigs.map((c) => c.selector).join('|');

    // 自身または祖先に既に同じデリゲートが登録済みなら何もしない
    // (document初期化済みの状態で子要素に再登録されると二重発火するのを防ぐ)
    if (this._isAlreadyRegistered(rootElement, selectorKey)) return;

    let registered = REGISTERED_ROOTS.get(rootElement);
    if (!registered) {
      registered = new Set();
      REGISTERED_ROOTS.set(rootElement, registered);
    }
    registered.add(selectorKey);

    // 1つのリスナーで複数セレクタを判定
    rootElement.addEventListener('click', (e) => {
      for (const { selector, attr } of selectorConfigs) {
        const target = e.target.closest(selector);
        if (!target || !rootElement.contains(target)) continue;

        const clickedValue = target.getAttribute(attr);
        if (!clickedValue) continue;

        e.preventDefault();

        // data-lightbox は属性値（グループ名）が一致する要素のみを同一ギャラリーとして扱う
        // (一覧画面等で別投稿の画像までスワイプできてしまう問題を防ぐ)
        const groupName = target.getAttribute('data-lightbox');
        const validElements = Array.from(
          rootElement.querySelectorAll(selector),
        ).filter((el) => {
          const value = el.getAttribute(attr);
          if (value === null || value === '') return false;
          if (target.hasAttribute('data-lightbox')) {
            return el.getAttribute('data-lightbox') === groupName;
          }
          return true;
        });

        const index = validElements.indexOf(target);
        void this._open(validElements, attr, Math.max(index, 0)).catch((error) => {
          console.error('[ImageZoomer] failed to open PhotoSwipe', error);
        });
        return;
      }
    });
  }

  _isAlreadyRegistered(rootElement, selectorKey) {
    // 自身から親方向に辿る (document.parentNode は null だが、document 自身もループ内で
    // チェックされるため、ループ後の追加チェックは不要)
    let node = rootElement;
    while (node) {
      const registered = REGISTERED_ROOTS.get(node);
      if (registered?.has(selectorKey)) return true;
      node = node.parentNode;
    }
    return false;
  }

  async _open(elements, imagePathAttribute, index) {
    const [{ default: PhotoSwipe }] = await Promise.all([
      import('photoswipe'),
      import('photoswipe/style.css'),
    ]);

    const dataSource = elements.map((el) => {
      const src = el.getAttribute(imagePathAttribute);
      const imgEl = el.tagName === 'IMG' ? el : el.querySelector('img');
      const width = imgEl?.naturalWidth || 0;
      const height = imgEl?.naturalHeight || 0;
      return { src, width, height, element: el };
    });

    const showArrows = elements.length > 1;

    const pswp = new PhotoSwipe({
      dataSource,
      index,
      closeOnVerticalDrag: true,
      pinchToClose: true,
      wheelToZoom: true,
      maxZoomLevel: 5,
      secondaryZoomLevel: 2,
      initialZoomLevel: 'fit',
      bgOpacity: 0.9,
      showHideAnimationType: 'fade',
      zoom: false,
      counter: false,
      arrowPrev: showArrows,
      arrowNext: showArrows,
    });

    // 画像サイズを動的に取得
    pswp.on('gettingData', (e) => {
      const item = e.data;
      if (item.width > 0) return;

      const img = new Image();
      img.onload = () => {
        if (!pswp.currSlide) return;
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;
        pswp.refreshSlideContent(e.index);
      };
      img.onerror = () => {
        // 読み込み失敗時は何もしない（PhotoSwipeのデフォルトエラー表示に任せる）
      };
      img.src = item.src;
    });

    pswp.init();
  }
}
